import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { lineKindOf } from "./plannerQuote";
import { LABOR_CATEGORIES } from "./plannerRates";

function todayStamp() {
    return new Date().toISOString().slice(0, 10);
}

export default function LogJobActionDialog({
    open,
    job,
    items = [],
    initialKind = "time",
    initialItemId = "",
    busy = false,
    onClose,
    onSave,
}) {
    const [kind, setKind] = useState(initialKind);
    const [itemId, setItemId] = useState(initialItemId);
    const [hours, setHours] = useState("1");
    const [title, setTitle] = useState("");
    const [qty, setQty] = useState("1");
    const [unitCost, setUnitCost] = useState("");
    const [body, setBody] = useState("");
    const [occurredOn, setOccurredOn] = useState(todayStamp());

    const laborItems = useMemo(
        () => (items || []).filter((row) => !row.archived && lineKindOf(row) === "labor"),
        [items]
    );
    const purchaseItems = useMemo(
        () => (items || []).filter((row) => !row.archived && lineKindOf(row) === "purchase"),
        [items]
    );
    const linked = useMemo(
        () => (kind === "time" ? laborItems : [...laborItems, ...purchaseItems]),
        [kind, laborItems, purchaseItems]
    );

    useEffect(() => {
        if (!open) return;
        setKind(initialKind === "material" ? "material" : "time");
        setItemId(initialItemId || "");
        setHours("1");
        setTitle("");
        setQty("1");
        setUnitCost("");
        setBody("");
        setOccurredOn(todayStamp());
    }, [open, initialKind, initialItemId]);

    const selected = linked.find((row) => row.id === itemId);
    const category = LABOR_CATEGORIES.find((row) => row.id === selected?.laborCategory);

    async function submit() {
        const payload = {
            jobId: job?.id,
            itemId: itemId || undefined,
            kind,
            occurredOn,
            body,
            title: title.trim() || selected?.title || (kind === "time" ? "Time" : "Extra material"),
            laborCategory: selected?.laborCategory || "",
        };
        if (kind === "time") payload.hours = Number(hours);
        else {
            payload.qty = Number(qty) || 1;
            payload.unitCost = Number(unitCost);
        }
        await onSave(payload);
    }

    return (
        <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
            <DialogTitle>{kind === "time" ? "Log time" : "Log extra material"}</DialogTitle>
            <DialogContent>
                <Stack spacing={1.75} sx={{ pt: 0.5 }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={kind}
                        onChange={(_e, next) => next && setKind(next)}
                    >
                        <ToggleButton value="time">Time</ToggleButton>
                        <ToggleButton value="material">Extra material</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="body2" color="text.secondary">
                        This stays on Job Actions. It does not change the customer quote. Extra materials hit internal
                        out-of-pocket; hours show against quoted labor.
                    </Typography>
                    {kind === "time" ? (
                        <TextField
                            label="Hours spent"
                            size="small"
                            type="number"
                            value={hours}
                            onChange={(e) => setHours(e.target.value)}
                            inputProps={{ min: 0, step: 0.25 }}
                            autoFocus
                        />
                    ) : (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                            <TextField
                                label="What did you buy?"
                                size="small"
                                fullWidth
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Cable, fastener pack, shop stock…"
                                autoFocus
                            />
                            <TextField
                                label="Qty"
                                size="small"
                                type="number"
                                value={qty}
                                onChange={(e) => setQty(e.target.value)}
                                sx={{ width: { sm: 100 } }}
                            />
                            <TextField
                                label="Unit cost"
                                size="small"
                                type="number"
                                value={unitCost}
                                onChange={(e) => setUnitCost(e.target.value)}
                                placeholder="12.50"
                                sx={{ width: { sm: 140 } }}
                            />
                        </Stack>
                    )}
                    <FormControl fullWidth size="small">
                        <InputLabel>{kind === "time" ? "Labor line" : "Link to item (optional)"}</InputLabel>
                        <Select
                            label={kind === "time" ? "Labor line" : "Link to item (optional)"}
                            value={itemId}
                            onChange={(e) => setItemId(e.target.value)}
                        >
                            <MenuItem value="">Whole job</MenuItem>
                            {linked.map((row) => (
                                <MenuItem key={row.id} value={row.id}>
                                    {row.title}
                                    {lineKindOf(row) === "labor" && row.laborCategory
                                        ? ` · ${LABOR_CATEGORIES.find((cat) => cat.id === row.laborCategory)?.label || row.laborCategory}`
                                        : ""}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {kind === "time" && category ? (
                        <Typography variant="caption" color="text.secondary">
                            Quoted {selected?.quoteOwnLaborHours || selected?.laborEstimate || 0} hrs · logged{" "}
                            {selected?.actualHours || 0} hrs · {category.label} ${category.hourlyRate}/hr
                        </Typography>
                    ) : null}
                    <TextField
                        label="Date"
                        size="small"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={occurredOn}
                        onChange={(e) => setOccurredOn(e.target.value)}
                    />
                    <TextField
                        label="Note"
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder={kind === "time" ? "What did you work on?" : "Vendor, why it was needed"}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button disabled={busy} onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="contained" disabled={busy} onClick={submit}>
                    Save log
                </Button>
            </DialogActions>
        </Dialog>
    );
}
